import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { FaCheck, FaCrown, FaLock, FaRedo } from "react-icons/fa";
import { PREMIUM_BENEFITS } from "../constants/subscriptionTiers";
import { useSubscription } from "../contexts/SubscriptionContext";
import { useAuth } from "../contexts/AuthContext";
import PlanCards from "../components/subscription/PlanCards";
import PublicLayout from "../components/site/PublicLayout";
import { Badge, Card, Container } from "../ui";
import { queueEvent } from "../analytics";
import { EVENTS } from "../analytics/events";

const Pricing = () => {
  const location = useLocation();
  const { isPremium, tier } = useSubscription();
  const { isAuthenticated, loading: authLoading } = useAuth();

  // Paywall funnel: this page IS the web paywall. `source` carries whatever
  // sent the user here (UpgradeModal passes it through navigation state).
  const source = location.state?.source || "pricing_page";
  const openedAtRef = useRef(Date.now());
  const shownRef = useRef(false);
  const purchaseStartedRef = useRef(false);

  // The page is public now: paywall_shown waits until we know whether this
  // is a signed-in account, and says so.
  useEffect(() => {
    if (authLoading || shownRef.current) return;
    shownRef.current = true;
    openedAtRef.current = Date.now();
    queueEvent(EVENTS.PAYWALL_SHOWN, {
      source,
      tier,
      signed_in: isAuthenticated,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading]);

  useEffect(
    () => () => {
      // Left without starting a checkout → dismissed (same semantics as the
      // app's paywall_dismissed on modal close).
      if (!shownRef.current || purchaseStartedRef.current) return;
      queueEvent(EVENTS.PAYWALL_DISMISSED, {
        source,
        time_on_paywall_ms: Date.now() - openedAtRef.current,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <PublicLayout>
      <Container className="py-10 md:py-16">
        {/* Phones: heading, plans, then benefits. Desktop: heading and
            benefits on one side, plans on the other. */}
        <div className="grid gap-8 lg:grid-cols-[1fr_1.05fr] lg:gap-x-12 lg:gap-y-8 items-start">
          <div className="text-center lg:text-right lg:col-start-1 lg:row-start-1">
            <Badge tone="copper" className="mb-4">
              <FaCrown aria-hidden="true" /> الاشتراك المميز
            </Badge>
            <h1 className="text-3xl md:text-5xl font-extrabold leading-tight">
              العب بلا حدود
            </h1>
            <p className="text-white/70 text-lg mt-3">
              ألعاب وفئات بلا حدود، وكل المزايا المميزة.
            </p>
            {isPremium && (
              <p className="mt-4 inline-flex items-center gap-2 rounded-xl bg-success/15 border border-success/40 px-4 py-2 font-bold text-success">
                <FaCheck aria-hidden="true" /> أنت مشترك مميز بالفعل
              </p>
            )}
          </div>

          <Card
            tone="solid"
            pad="lg"
            className="shadow-2xl shadow-black/30 lg:col-start-2 lg:row-start-1 lg:row-span-2"
          >
            <h2 className="text-xl font-extrabold mb-5">اختر باقتك</h2>
            <PlanCards
              source={source}
              onPurchaseStart={() => {
                purchaseStartedRef.current = true;
              }}
            />
          </Card>

          <div className="lg:col-start-1 lg:row-start-2">
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1 max-w-md mx-auto lg:mx-0">
              {PREMIUM_BENEFITS.map((b) => (
                <li key={b.text} className="flex items-center gap-3 text-white/90">
                  <span className="w-7 h-7 shrink-0 rounded-lg bg-success/15 text-success flex items-center justify-center">
                    <FaCheck className="text-xs" aria-hidden="true" />
                  </span>
                  {b.text}
                </li>
              ))}
            </ul>

            <ul className="mt-8 space-y-2 text-sm text-white/60 max-w-md mx-auto lg:mx-0">
              <li className="flex items-center gap-2">
                <FaLock aria-hidden="true" /> دفع آمن عبر MyFatoorah
              </li>
              <li className="flex items-center gap-2">
                <FaRedo aria-hidden="true" /> دفعة واحدة، بدون تجديد تلقائي
              </li>
            </ul>
          </div>
        </div>
      </Container>
    </PublicLayout>
  );
};

export default Pricing;
